import { QueryClient } from '@tanstack/react-query';
import { ApiError, tokenStore } from '@/services/api-client';

/**
 * Query defaults.
 *
 * The retry policy is the part worth reading. Retrying a 401, 403 or 404 is
 * pointless — the answer will not change — and retrying a 429 actively makes
 * the rate-limit situation worse. Only genuinely transient failures are retried.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Church directory data changes rarely within a session. Thirty seconds
      // of staleness avoids a refetch on every navigation.
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: (failureCount, error) => {
        if (error instanceof ApiError) {
          if (error.status === 401 || error.status === 403 || error.status === 404) return false;
          if (error.status === 429) return false;
        }
        return failureCount < 2;
      },
      // Refetching on focus is right for a shared workstation where another
      // member of staff may have changed something.
      refetchOnWindowFocus: true,
    },
    mutations: {
      // Never retried automatically. A retried create makes two people.
      retry: false,
    },
  },
});

/**
 * Clears cached data on sign-out.
 *
 * Without this, the next person to sign in on a shared church workstation sees
 * the previous user's directory until each query happens to refetch.
 */
export function clearSessionCache(): void {
  queryClient.clear();
  tokenStore.clear();
}
